import { Fragment, useEffect, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import axios from "axios";
import { Link } from "react-router-dom";
import config from "../../../../config.json";
import Categories from "../AppsMenu/Shop/Category/Categories";
import DisplayOrientationBulkBar from "./DisplayOrientationBulkBar";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import "./kitchenLuxe.css";

const CategoryGrid = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [isError, setIsError] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${config.dev}/v2/categories/retrieve`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      });
      const data = response?.data || [];
      setCategories(data);
      setFilteredCategories(data);
      setIsError(false);
    } catch (err) {
      setError("Failed to fetch categories. Please try again later.");
      setIsError(true);
      console.error("Error fetching products:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const results = categories.filter((category) => {
      const matchesDetails = category.categoryDetails
        ? category.categoryDetails.some((detail) =>
            ["categoryTitle", "categoryDescription"].some((key) =>
              detail[key]?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : false;

      return matchesDetails;
    });

    setFilteredCategories(results);
  }, [searchTerm, categories]);

  const visibleIds = filteredCategories
    .filter((category) => !category.isArchived)
    .map((category) => category._id);
  const selectedVisible = selectedIds.filter((id) => visibleIds.includes(id));
  const allSelected =
    visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const applyOrientation = async (orientation, all) => {
    setBulkSaving(true);
    try {
      await axios.put(
        `${config.dev}/v2/categories/display-orientation`,
        all ? { all: true, displayOrientation: orientation } : { ids: selectedVisible, displayOrientation: orientation },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
          },
        }
      );
      setSelectedIds([]);
      await fetchCategories();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update orientation.");
      setIsError(true);
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <Fragment>
      <div className="kitchen-page" data-tour="tour-page-categories">
        <PageTitle activeMenu={t("categories")} motherMenu={t("kitchen")} />
        <div className="kitchen-toolbar">
          <div className="kitchen-toolbar__search">
            <input
              type="text"
              className="form-control"
              placeholder={t("searchCat")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="kitchen-toolbar__actions">
            <Link to={"/add-category"} className="kitchen-toolbar__add">
              <i className="fas fa-plus" />
              {t("addNewCat")}
            </Link>
          </div>
          <DisplayOrientationBulkBar
            selectedCount={selectedVisible.length}
            totalCount={visibleIds.length}
            allSelected={allSelected}
            saving={bulkSaving}
            onToggleSelectAll={() =>
              setSelectedIds(allSelected ? [] : visibleIds)
            }
            onApplySelected={(orientation) => applyOrientation(orientation, false)}
            onApplyAll={(orientation) => applyOrientation(orientation, true)}
          />
        </div>
        <div className="row kitchen-grid">
          {filteredCategories.length === 0 ? (
            <div className="col-12">
              <div className="kitchen-empty" role="alert">
                {t("noDataFound")}
              </div>
            </div>
          ) : (
            filteredCategories.map((category) => (
              <Categories
                key={category._id}
                fetchCategories={fetchCategories}
                category={category}
                selected={selectedIds.includes(category._id)}
                onToggleSelect={toggleSelect}
              />
            ))
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default CategoryGrid;
